// Using native fetch API instead of axios
import { AUTH_API_BASE_URL, SESSION_API_BASE_URL } from '../config/api';
import { authFetch, endSession, SESSION_HEADERS, storeSession } from './session';

const API_URL = AUTH_API_BASE_URL;

// Helper function to handle fetch calls
const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
  const response = await authFetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    },
  });

  // Final: authFetch already tried to renew the session and told the app.
  if (response.status === 401) {
    throw new Error('Authentication expired. Please login again.');
  }

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response.json();
};

// Helper function for unauthenticated endpoints to avoid auth-error redirects
const fetchWithoutAuth = async (url: string, options: RequestInit = {}) => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response.json();
};

export interface TestNumber {
  id: number;
  phone_raw: string | null;
  phone_normalized: string;
  label: string | null;
  verified: boolean;
  created_at: string;
  /** Free test interactions used this calendar week (voice + whatsapp). */
  used_this_week?: number;
  /** Weekly free quota (5). */
  weekly_limit?: number;
}

/** One agent phone number and the salons it is responsible for. */
export interface AgentNumberConfig {
  phone_number: string;
  /** "voice" (Twilio), "whatsapp", or "both" when one line serves both. */
  channel: 'voice' | 'whatsapp' | 'both';
  /** Assigned TimeGlobe siteCds. Empty means the number serves all salons. */
  site_cds: string[];
  /** Optional greeting name for this number ("Filiale Süd"). */
  display_name: string | null;
  /** Assigned siteCds that no longer exist in TimeGlobe. */
  unknown_site_cds: string[];
  /** True when this number has its own WhatsApp connection. */
  has_own_whatsapp: boolean;
  /**
   * True when the line runs on our Twilio account — only then can we intercept
   * Meta's verification call/SMS and show the code during a WhatsApp connect.
   */
  is_twilio: boolean;
}

export interface AgentNumberConfigList {
  success: boolean;
  numbers: AgentNumberConfig[];
  /** All salons of the tenant, for the assignment picker. */
  sites: { siteCd: string; siteNm: string }[];
  /** One WhatsApp line for the company, or one per location. */
  whatsapp_mode: 'central' | 'per_site';
  error?: string | null;
}

export interface SalonTransferNumber {
  site_cd: string;
  phone_number: string | null;
}

export interface SalonTransferNumberList {
  success: boolean;
  numbers: SalonTransferNumber[];
  /** All salons of the tenant, so every one gets a labelled row. */
  sites: { siteCd: string; siteNm: string }[];
  /** Company-wide number used by every salon without its own. */
  default_phone: string | null;
  error?: string | null;
}

/** Whom a service group is shown to in the overview after "Termin buchen". */
export type ServiceAudience = 'female' | 'male' | 'both' | 'none';

export interface ServiceGroupService {
  item_no: number;
  name: string;
  /** false: add-on / optional service, bookable only together with a main service. */
  standalone: boolean;
}

export interface ServiceGroup {
  /** Exactly as TimeGlobe spells it — the name is the key. */
  name: string;
  /** What the agent uses: the salon's assignment, else what the name states. */
  audience: ServiceAudience;
  /** What the group's own name states — the value a reset goes back to. */
  default_audience: ServiceAudience;
  services: ServiceGroupService[];
}

export interface ServiceGroupList {
  success: boolean;
  /** All salons of the tenant, for the salon picker. */
  sites: { siteCd: string; siteNm: string }[];
  /** The salon `groups` belongs to; null when the tenant has no salon. */
  site_cd: string | null;
  groups: ServiceGroup[];
}

const authService = {
  // Register a new business
  register: async (userData: {
    business_name: string;
    email: string;
    phone_number: string;
    password: string;
  }) => {
    try {
      return await fetchWithoutAuth(`${API_URL}/register`, {
        method: 'POST',
        body: JSON.stringify(userData),
      });
    } catch (error) {
      throw error;
    }
  },

  // Login
  login: async (credentials: { username: string; password: string }) => {
    try {
      // For form-urlencoded content type
      const formData = new URLSearchParams();
      Object.entries(credentials).forEach(([key, value]) => {
        formData.append(key, value);
      });
      
      const response = await fetch(`${SESSION_API_BASE_URL}/login`, {
        method: 'POST',
        // The response sets the session cookie that keeps this login alive —
        // only for this call: a password check without the header gets none.
        credentials: 'include',
        headers: {
          ...SESSION_HEADERS,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.access_token) {
        storeSession(data.access_token, data.expires_in);
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  },

  // Verify OTP
  verifyOtp: async (data: { email: string; otp: string }) => {
    try {
      return await fetchWithoutAuth(`${API_URL}/verify-otp`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      throw error;
    }
  },

  // Resend OTP
  resendOtp: async (data: { email: string }) => {
    try {
      return await fetchWithoutAuth(`${API_URL}/resend-otp`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      throw error;
    }
  },

  // Forgot password
  forgotPassword: async (data: { email: string }) => {
    try {
      return await fetchWithoutAuth(`${API_URL}/forget-password`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      throw error;
    }
  },

  // Reset password
  resetPassword: async (data: { business_id: string; token: string; new_password: string }) => {
    try {
      return await fetchWithoutAuth(`${API_URL}/reset-password`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      throw error;
    }
  },

  // Get business profile
  getBusinessProfile: async (signal?: AbortSignal) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      // Assuming the /business/me endpoint no longer returns the auth key
      return await fetchWithAuth(`${API_URL}/business/me`, {
        method: 'GET',
        signal,
      });
    } catch (error) {
      throw error;
    }
  },

  // Get TimeGlobe API key for the business
  getTimeglobeAuthKey: async (): Promise<{ timeglobe_auth_key: string; customer_cd: string }> => {
    try {
      return await fetchWithAuth(`${API_URL}/business/timeglobe-key`, {
        method: 'GET',
      });
    } catch (error) {
      console.error('Error fetching TimeGlobe Auth Key:', error);
      throw error;
    }
  },

  // Update business profile
  updateBusinessProfile: async (data: {
    business_name?: string;
    phone_number?: string;
    timeglobe_auth_key?: string;
    whatsapp_number?: string;
  }) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      return await fetchWithAuth(`${API_URL}/business/update`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    } catch (error) {
      throw error;
    }
  },

  // Logout: forget the login on this device and end the session on the server
  logout: (): Promise<void> => endSession(),

  // Get TimeGlobe API key for the business
  getBusinessTimeglobeKey: async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      return await fetchWithAuth(`${API_URL}/business/timeglobe-key`, {
        method: 'GET',
      });
    } catch (error) {
      throw error;
    }
  },

  // Validate TimeGlobe API key
  validateTimeglobeKey: async (authKey: string) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      return await fetchWithAuth(`${API_URL}/validate-timeglobe-key`, {
        method: 'POST',
        body: JSON.stringify({ auth_key: authKey }),
      });
    } catch (error) {
      throw error;
    }
  },

  // Get business information
  getBusinessInfo: async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      return await fetchWithAuth(`${API_URL}/business/info`, {
        method: 'GET',
      });
    } catch (error) {
      throw error;
    }
  },

  // Update business information
  updateBusinessInfo: async (data: {
    business_name?: string;
    tax_id?: string;
    street_address?: string;
    postal_code?: string;
    city?: string;
    country?: string;
    contact_person?: string;
    phone_number?: string;
    email?: string;
  }) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      return await fetchWithAuth(`${API_URL}/update-business-info`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      throw error;
    }
  },

  // Delete business info fields
  deleteBusinessInfoFields: async (fields: string[]) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      return await fetchWithAuth(`${API_URL}/delete-business-info`, {
        method: 'DELETE',
        body: JSON.stringify({ fields }),
      });
    } catch (error) {
      throw error;
    }
  },

  // --- Per-account test numbers (SMS-OTP verified, business-scoped non_pay) ---
  getTestNumbers: async (): Promise<TestNumber[]> => {
    return await fetchWithAuth(`${API_URL}/business/test-numbers`, {
      method: 'GET',
    });
  },

  // Register a test number; backend sends an SMS OTP to it.
  addTestNumber: async (phone: string, label?: string): Promise<TestNumber> => {
    return await fetchWithAuth(`${API_URL}/business/test-numbers`, {
      method: 'POST',
      body: JSON.stringify({ phone, label: label || undefined }),
    });
  },

  // Verify a test number's SMS OTP.
  verifyTestNumber: async (phone: string, otpCode: string): Promise<TestNumber> => {
    return await fetchWithAuth(`${API_URL}/business/test-numbers/verify`, {
      method: 'POST',
      body: JSON.stringify({ phone, otp_code: otpCode }),
    });
  },

  deleteTestNumber: async (id: number): Promise<{ success: boolean }> => {
    return await fetchWithAuth(`${API_URL}/business/test-numbers/${id}`, {
      method: 'DELETE',
    });
  },

  // --- Agent numbers: which salons each number serves ("Teilagenten") ---
  getNumberConfigs: async (): Promise<AgentNumberConfigList> => {
    return await fetchWithAuth(`${API_URL}/business/number-configs`, {
      method: 'GET',
    });
  },

  /** Assign salons + an optional greeting name to one agent number. */
  upsertNumberConfig: async (
    phoneNumber: string,
    siteCds: string[],
    displayName?: string | null,
  ): Promise<AgentNumberConfig> => {
    return await fetchWithAuth(`${API_URL}/business/number-configs`, {
      method: 'PUT',
      body: JSON.stringify({
        phone_number: phoneNumber,
        site_cds: siteCds,
        display_name: displayName || null,
      }),
    });
  },

  // --- Leistungen: whom each service group of a salon is shown to ---
  /** One salon's groups; without `siteCd` the tenant's first salon. */
  getServiceGroups: async (siteCd?: string): Promise<ServiceGroupList> => {
    const query = siteCd ? `?site_cd=${encodeURIComponent(siteCd)}` : '';
    return await fetchWithAuth(`${API_URL}/business/service-groups${query}`, {
      method: 'GET',
    });
  },

  /** Assign the given groups of one salon; groups not sent stay as they are. */
  saveServiceGroups: async (
    siteCd: string,
    groups: { name: string; audience: ServiceAudience }[],
  ): Promise<{ success: boolean; site_cd: string }> => {
    return await fetchWithAuth(`${API_URL}/business/service-groups`, {
      method: 'PUT',
      body: JSON.stringify({ site_cd: siteCd, groups }),
    });
  },

  // --- Per-salon forwarding numbers: where "Mitarbeiter sprechen" rings ---
  getSalonTransferNumbers: async (): Promise<SalonTransferNumberList> => {
    return await fetchWithAuth(`${API_URL}/business/salon-transfer-numbers`, {
      method: 'GET',
    });
  },

  /** Set one salon's forwarding number; an empty string clears it again. */
  upsertSalonTransferNumber: async (
    siteCd: string,
    phoneNumber: string,
  ): Promise<SalonTransferNumber> => {
    return await fetchWithAuth(`${API_URL}/business/salon-transfer-numbers`, {
      method: 'PUT',
      body: JSON.stringify({ site_cd: siteCd, phone_number: phoneNumber }),
    });
  },

  // Validate password for auth key visibility
  validatePassword: async (password: string) => {
    try {
      // Get current user email from storage
      const currentUserStr = sessionStorage.getItem('currentUser');
      const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null;
      
      if (!currentUser?.email) {
        console.error('No user email found');
        return false;
      }

      // Use the same format as login
      const formData = new URLSearchParams();
      formData.append('username', currentUser.email);
      formData.append('password', password);
      
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });
      
      if (!response.ok) {
        return false;
      }
      
      const data = await response.json();
      return data.access_token ? true : false;
    } catch (error) {
      console.error('Password validation failed:', error);
      return false;
    }
  },
};

export default authService; 